/* Copyright (c) 2020-present CloudIO Inc */

export interface EventMap {
  [key: string]: Record<string, unknown>;
}

export class PubSub<T = EventMap> {
  map = new Map<
    keyof T,
    ((event: keyof T, props: T[keyof T]) => Promise<void>)[]
  >();

  set = new Set<string>();

  count = new Map<keyof T, number>();

  data = new Map<keyof T, unknown>();

  destroy = () => {
    this.map.clear();
    this.set.clear();
    this.count.clear();
    this.data.clear();
  };

  pub = async <K extends keyof T>(
    event: K,
    props: T[K],
    deferrable = false,
    deferrableOnce = false,
  ) => {
    const listeners = this.map.get(event) ?? [];
    const len = listeners.length;
    // console.log('publishing event', event, { len, deferrable });
    let onceCalled = false;
    if (len > 0) {
      const all = [];
      for (let i = 0; i < len; i++) {
        all.push(listeners[i](event, props));
      }
      await Promise.allSettled(all);
      onceCalled = true;
    }
    if (deferrable || (deferrableOnce && !onceCalled)) {
      this.data.set(event, props);
    }
  };

  sub = <K extends keyof T>(
    event: K | K[],
    listener: (_event: K, props: T[K]) => Promise<void>,
    deferrable = false,
    deferrableOnce = false,
  ): (() => void) => {
    if (!Array.isArray(event)) {
      event = [event];
    }
    event.forEach((e) => {
      // console.log('sub event', event, { deferrable, deferrableOnce });
      let listeners = this.map.get(e);
      if (!listeners) {
        listeners = [];
        this.map.set(e, listeners);
      }
      // @ts-ignore todo
      listeners.push(listener);

      if (deferrable || deferrableOnce) {
        const val = this.data.get(e);
        if (val) {
          // defer to avoid calling the listener immediately
          setTimeout(() => {
            listener(e, val as T[K]);
          }, 1);
          // leave the event in the data map so it can be used for future subscribers
          if (deferrableOnce) {
            this.data.delete(e);
          }
        }
      }
    });

    return () => {
      (event as K[]).forEach((e) => {
        let _listeners = this.map.get(e);
        if (_listeners) {
          _listeners = _listeners.filter((l) => l !== listener);
          this.map.set(e, _listeners);
        }
      });
    };
  };
}

export default new PubSub();
